import React, { useState } from 'react';
import { Printer, Loader2, Barcode } from 'lucide-react';
import api from '../api';

export default function BulkBarcodePrint() {
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [printerType, setPrinterType] = useState('dymo');

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await api.generateBulkBarcodes([]);
      setBarcodes(result.barcodes || []);
      setGenerated(true);
    } catch (err) {
      alert('Failed to generate barcodes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (printerType === 'dymo') {
      const w = window.open('', '_blank');
      const labels = barcodes.map(bc => `
        <div class="label">
          <div class="product-name">${(bc.name || '').replace(/"/g, '&quot;').replace(/</g, '&lt;')}</div>
          <div class="barcode-row"><img src="${bc.image_data_url}" /></div>
          <div class="sku">${bc.sku}</div>
        </div>
      `).join('');
      w.document.write(`<html><head><title>Bulk Barcodes - Dymo</title><style>
@page { size: 3.5in 1.125in; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
.label { width: 3.5in; height: 1.125in; padding: 0.06in 0.1in; display: flex; flex-direction: column; justify-content: center; overflow: hidden; page-break-after: always; }
.product-name { font-size: 8pt; font-weight: bold; line-height: 1.15; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 0.02in; }
.barcode-row { display: flex; align-items: center; justify-content: center; }
img { max-width: 3.0in; max-height: 0.52in; height: 0.52in; object-fit: contain; }
.sku { font-family: 'Courier New', monospace; font-size: 7.5pt; text-align: center; margin-top: 0.02in; letter-spacing: 0.03em; }
      </style></head><body>${labels}</body></html>`);
      w.document.close();
      w.onload = () => { w.focus(); w.print(); };
    } else {
      window.print();
    }
  };

  return (
    <div data-testid="bulk-barcode-page">
      {/* Controls - hidden when printing */}
      <div className="print:hidden mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Barcode className="h-6 w-6 text-primary" /> Bulk Barcode Generator
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Generate and print barcodes for all products in inventory</p>
          </div>
          <div className="flex gap-3 items-center">
            <select value={printerType} onChange={(e) => setPrinterType(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-background" data-testid="bulk-printer-type">
              <option value="dymo">Dymo 30252 Labels</option>
              <option value="regular">Regular Printer</option>
            </select>
            <button onClick={handleGenerate} disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
              data-testid="generate-all-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Barcode className="h-4 w-4" />}
              {loading ? 'Generating...' : 'Generate All Barcodes'}
            </button>
            {generated && (
              <button onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-muted font-medium"
                data-testid="print-all-btn">
                <Printer className="h-4 w-4" /> Print All ({barcodes.length})
              </button>
            )}
          </div>
        </div>
        {generated && <p className="mt-3 text-sm text-muted-foreground">{barcodes.length} barcodes generated</p>}
      </div>

      {/* Barcode Grid */}
      {generated && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 print:grid-cols-4 print:gap-2">
          {barcodes.map((bc, i) => (
            <div key={i} className="border rounded-lg p-3 text-center bg-white print:border-gray-300 print:p-2 break-inside-avoid"
              data-testid={`barcode-card-${i}`}>
              <div className="text-xs font-bold truncate mb-1" style={{color: '#111'}} title={bc.name}>
                {bc.name}
              </div>
              <img src={bc.image_data_url} alt={bc.sku} className="mx-auto max-w-full h-auto" style={{maxHeight: '60px'}} />
              <div className="text-[10px] font-mono text-gray-500 mt-1">{bc.sku}</div>
            </div>
          ))}
        </div>
      )}

      {!generated && !loading && (
        <div className="text-center py-16 text-muted-foreground">
          <Barcode className="h-16 w-16 mx-auto mb-4 opacity-20" />
          <p>Click "Generate All Barcodes" to create printable barcodes for every product</p>
        </div>
      )}

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:grid-cols-4 { grid-template-columns: repeat(4, 1fr) !important; }
          .print\\:gap-2 { gap: 0.5rem !important; }
          .print\\:p-2 { padding: 0.5rem !important; }
          .print\\:border-gray-300 { border-color: #d1d5db !important; }
          body { margin: 0; }
        }
      `}</style>
    </div>
  );
}
